import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from 'components/AppIcon';
import { Spotlight } from '../../../components/ui/spotlight';
import { TextGenerateEffect } from '../../../components/ui/text-generate-effect';
import Hero3DModel from '../../../components/ui/Avatar3D';
import api from '../../../lib/api';

const HeroSection = () => {
  const [currentRole, setCurrentRole] = useState(0);
  const [personalInfo, setPersonalInfo] = useState({
    name: "Nikunj Navdiya",
    tagline: "Multi-dimensional Developer",
    introduction: `Welcome to my digital universe! I'm a passionate technologist who thrives at the intersection of community building, blockchain innovation, and software craftsmanship.`,
    detailedIntro: "With over 3+ years of experience across three distinct professional worlds, I bring unique perspectives to every project I touch.",
    location: "India",
    availability: "Available for new opportunities",
    roles: ["Community Builder", "Web3 Developer", "Software Engineer"]
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchPersonalInfo = async () => {
      try {
        setLoading(true);
        const response = await api.personalInfo.get();
        if (response.personalInfo) {
          setPersonalInfo(response.personalInfo);
        }
      } catch (err) {
        console.error('Error fetching personal info:', err);
        setError('Failed to load personal information');
        // Keep using default data on error
      } finally {
        setLoading(false);
      }
    };
    
    fetchPersonalInfo();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentRole((prev) => (prev + 1) % personalInfo.roles.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [personalInfo.roles]);

  const floatingIcons = [
    { icon: "Users", color: "text-blue-600", delay: 0 },
    { icon: "Code", color: "text-green-600", delay: 0.5 },
    { icon: "Zap", color: "text-yellow-600", delay: 1 }
  ];

  if (loading) {
    return (
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <div className="flex flex-col items-center">
          <Icon name="Loader" size={40} className="text-primary animate-spin mb-4" />
          <p className="font-body text-text-secondary">Loading...</p>
        </div>
      </section>
    );
  }

  if (error) {
    console.warn('Using default data due to error:', error);
  }

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Lightweight Background Effects */}
      <div className="absolute inset-0 w-full h-full">
        {/* Simplified Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.02] bg-[linear-gradient(to_right,#000_1px,transparent_1px),linear-gradient(to_bottom,#000_1px,transparent_1px)] bg-[size:40px_40px]" />
        
        {/* Single Spotlight */}
        <Spotlight className="-top-40 left-0 md:-top-20 md:left-60" fill="#3b82f6" />
        
        {/* Minimal Gradient Orbs */}
        {/* <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-blue-400/10 to-purple-400/10 rounded-full blur-3xl"></div> */}
        {/* <div className="absolute bottom-20 right-10 w-40 h-40 bg-gradient-to-r from-pink-400/10 to-orange-400/10 rounded-full blur-3xl"></div> */}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Optimized Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left space-y-8"
          >
            {/* Simple Status Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center justify-center lg:justify-start"
            >
              <div className="flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full border border-gray-200 shadow-sm">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="font-caption text-gray-600 text-sm">
                  {personalInfo.availability}
                </span>
              </div>
            </motion.div>

            {/* Enhanced Name */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-primary mb-4"
            >
              Hi, I'm{' '}
              <span className="bg-gradient-to-r from-accent to-warning bg-clip-text text-transparent">
                {personalInfo.name}
              </span>
            </motion.h1>

            {/* Simplified Role Animation */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="space-y-4"
            >
              <div className="flex flex-col lg:flex-row lg:items-center space-y-2 lg:space-y-0 lg:space-x-4">
                <span className="font-body text-xl md:text-2xl text-gray-600">I'm a</span>
                <div className="relative">
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={currentRole}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.5 }}
                      className="inline-block font-bold text-2xl md:text-3xl bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent"
                    >
                      {personalInfo.roles[currentRole]}
                    </motion.span>
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>

            {/* Introduction with TextGenerateEffect */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <TextGenerateEffect
                words={personalInfo.introduction}
                className="font-body text-sm text-gray-600 leading-relaxed max-w-2xl mb-4"
              />
              <p className="font-body text-gray-500 leading-relaxed max-w-2xl">
                {personalInfo.detailedIntro}
              </p>
            </motion.div>

            {/* Location Badge */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="flex items-center justify-center lg:justify-start space-x-2"
            >
              <div className="flex items-center space-x-2 bg-gray-100 px-3 py-2 rounded-full border border-gray-200">
                <Icon name="MapPin" size={16} className="text-blue-600" />
                <span className="font-caption text-gray-600 text-sm">
                  {personalInfo.location}
                </span>
              </div>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >

                <a className="bg-white text-gray-900 px-8 py-4 rounded-xl font-body font-medium transition-all duration-200 transform hover:scale-105 flex items-center space-x-2 shadow-lg" onClick={()=>{
                document.querySelector('#personality-worlds').scrollIntoView({ behavior: 'smooth' });
                }}>
                  <span>Explore My Worlds</span>
                  <Icon name="ArrowRight" size={16} />
                </a>
          
                <a onClick={()=>{
                  document.querySelector('#book-call').scrollIntoView({ behavior: 'smooth' });
                }} className="border border-gray-300 bg-white/80 backdrop-blur-sm text-gray-700 px-8 py-4 rounded-xl font-body font-medium hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 transform hover:scale-105 flex items-center space-x-2 shadow-sm">
                  <Icon name="Calendar" size={16} />
                  <span>Book a Call</span>
                </a>
              
            </motion.div>
          </motion.div>

          {/* 3D Avatar Profile Section */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative"
          >
            <div className="relative w-80 h-80 md:w-96 md:h-96 mx-auto">
              {/* Reduced Floating Icons */}
              {floatingIcons.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ 
                    opacity: 1, 
                    scale: 1,
                    y: [-5, 5, -5]
                  }}
                  transition={{ 
                    opacity: { delay: item.delay, duration: 0.5 },
                    scale: { delay: item.delay, duration: 0.5 },
                    y: { 
                      duration: 2 + index * 0.5, 
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: item.delay
                    }
                  }}
                  className="absolute w-12 h-12 rounded-full bg-white border border-gray-200 shadow-lg flex items-center justify-center z-10"
                  style={{
                    top: `${30 + (index * 20)}%`,
                    right: index % 2 === 0 ? '-8%' : 'auto',
                    left: index % 2 === 1 ? '-8%' : 'auto',
                  }}
                  whileHover={{ scale: 1.1 }}
                >
                  <Icon name={item.icon} size={20} className={item.color} />
                </motion.div>
              ))}

              
                <div className="relative w-full h-full rounded-3xl overflow-visible bg-transparent">
                  {/* 3D Avatar */}
                  <Hero3DModel />
                </div>

              {/* Simple Rotating Ring */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 rounded-full border-2 border-dashed border-blue-200/50 pointer-events-none"
              />
              
              {/* Glow Effect */}
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-400/20 via-purple-400/20 to-cyan-400/20 rounded-full blur-xl -z-10 animate-pulse"></div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Simplified Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 hidden md:block left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex flex-col items-center space-y-2"
        >
          <span className="font-caption text-gray-500 text-xs">Scroll to explore</span>
          <div className="w-6 h-10 border-2 border-gray-300 rounded-full flex justify-center">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-1 h-3 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full mt-2"
            />
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;